import { PayloadAction, createSlice, nanoid } from '@reduxjs/toolkit';
import { LoginDataCrm } from '../../interface/crm/LoginDataCrm';
import {
  DettaglioEventoInterface,
  ListaAzioniInterface,
  ListaGruppiFiltroInterface,
  ListaGruppoCondizioniFiltroOrInterface,
  ListaMezziInterface,
  getFiltriDataInterface,
} from '../../interface/crm/DettaglioEventoInterfaces';
import { AppDispatch } from '..';
import { CrmTreeItemInterface } from '@focus.cloud/src/crm/menu/CrmLayoutMenu';

interface CrmEventInfo {
  isCampagna: boolean;
  isFornitori: boolean;
  isTemplate: boolean;
}

export interface ContatoriCrmInterface {
  totaleAttivitaGiornaliera: number;
  totaleCampagneFornitori: number;
  totaleCampagneFornitoriAttivate: number;
  totaleCampagnePersonalizzate: number;
  totaleCampagnePersonalizzateAttivate: number;
  totaleClientiInSospeso: number;
  totaleConflitti: number;
  totaleEventiAzioniCanalEsterno: number;
  totaleEventiFornitori: number;
  totaleEventiFornitoriAttivati: number;
  totaleEventiPersonalizzati: number;
  totaleEventiPersonalizzatiAttivati: number;
  totaleTelefonate: number;
  totaleTemplateCampagna: number;
  totaleTemplateEvento: number;
  warningFornitoreCampagna: boolean;
  warningFornitoreCiclica: boolean;
}

export interface ImoContatoriInterface {
  allarmi: number;
  daleggere: number;
  richiesteapp: number;
  richiestelac: number;
  smsuscitaerrore: number;
}

export interface CrmRootSliceState {
  loginData: LoginDataCrm;
  dettaglioEvento: DettaglioEventoInterface;
  editEventoCrm: boolean;
  crmOpenEventInfo: CrmEventInfo;
  dettaglioEventoEdit: DettaglioEventoInterface;
  editCrmNuovo: boolean;
  openDetailCrmId: number;
  isDuplicatedTemplate: number;
  codiceFilialeClienteAppImoSelezionato: string;
  isComunicazioniEditingRow: boolean;
  popupCrmAlertCalled: boolean;
  isCrmOpened: boolean;
  isMenuOpened: boolean;
  imoContatori: ImoContatoriInterface;
  crmContatori: ContatoriCrmInterface;
  disabilitaRamiCrm: string;
  firstTreeNode: string;
  crmTree: CrmTreeItemInterface[];
}

const initialState: CrmRootSliceState = {
  loginData: {},
  firstTreeNode: '',
  dettaglioEvento: undefined,
  dettaglioEventoEdit: undefined,
  editEventoCrm: false,
  crmOpenEventInfo: {
    isCampagna: false,
    isTemplate: false,
    isFornitori: false,
  },
  editCrmNuovo: false,
  openDetailCrmId: undefined,
  isDuplicatedTemplate: undefined,
  codiceFilialeClienteAppImoSelezionato: '',
  isComunicazioniEditingRow: false,
  popupCrmAlertCalled: false,
  isCrmOpened: false,
  isMenuOpened: true,
  imoContatori: {
    allarmi: 0,
    daleggere: 0,
    richiesteapp: 0,
    richiestelac: 0,
    smsuscitaerrore: 0,
  },
  crmContatori: {
    totaleAttivitaGiornaliera: 0,
    totaleCampagneFornitori: 0,
    totaleCampagneFornitoriAttivate: 0,
    totaleCampagnePersonalizzate: 0,
    totaleCampagnePersonalizzateAttivate: 0,
    totaleClientiInSospeso: 0,
    totaleConflitti: 0,
    totaleEventiAzioniCanalEsterno: 0,
    totaleEventiFornitori: 0,
    totaleEventiFornitoriAttivati: 0,
    totaleEventiPersonalizzati: 0,
    totaleEventiPersonalizzatiAttivati: 0,
    totaleTelefonate: 0,
    totaleTemplateCampagna: 0,
    totaleTemplateEvento: 0,
    warningFornitoreCampagna: false,
    warningFornitoreCiclica: false,
  },
  disabilitaRamiCrm: '',
  crmTree: [],
};

export const CrmRootSlice = createSlice({
  name: 'crmroot',
  initialState,
  reducers: {
    setCrmLoginData: (
      state: CrmRootSliceState,
      action: PayloadAction<LoginDataCrm>
    ) => {
      state.loginData = action.payload;
    },
    setDettaglioEvento: (
      state: CrmRootSliceState,
      action: PayloadAction<DettaglioEventoInterface>
    ) => {
      state.dettaglioEvento = action.payload;
    },
    setDettaglioEventoEdit: (
      state: CrmRootSliceState,
      action: PayloadAction<DettaglioEventoInterface>
    ) => {
      state.dettaglioEventoEdit = action.payload;
    },
    setEditEventoCrm: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.editEventoCrm = action.payload;
    },
    setCrmOpenEventInfo: (
      state: CrmRootSliceState,
      action: PayloadAction<CrmEventInfo>
    ) => {
      state.crmOpenEventInfo = action.payload;
    },
    setEditCrmNuovo: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.editCrmNuovo = action.payload;
    },
    setOpenDetailCrmId: (
      state: CrmRootSliceState,
      action: PayloadAction<number>
    ) => {
      state.openDetailCrmId = action.payload;
    },
    setIsDuplicatedTemplate: (
      state: CrmRootSliceState,
      action: PayloadAction<number>
    ) => {
      state.isDuplicatedTemplate = action.payload;
    },
    setCodiceFilialeClienteAppImoSelezionato: (
      state: CrmRootSliceState,
      action: PayloadAction<string>
    ) => {
      state.codiceFilialeClienteAppImoSelezionato = action.payload;
    },
    setIsComunicazioneEditingRow: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.isComunicazioniEditingRow = action.payload;
    },
    setPopupCrmAlertCalled: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.popupCrmAlertCalled = action.payload;
    },
    setIsCrmOpened: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.isCrmOpened = action.payload;
    },
    setIsMenuOpened: (
      state: CrmRootSliceState,
      action: PayloadAction<boolean>
    ) => {
      state.isMenuOpened = action.payload;
    },
    setImoContatori: (
      state: CrmRootSliceState,
      action: PayloadAction<ImoContatoriInterface>
    ) => {
      state.imoContatori = action.payload;
    },
    setCrmContatori: (
      state: CrmRootSliceState,
      action: PayloadAction<ContatoriCrmInterface>
    ) => {
      state.crmContatori = action.payload;
    },
    setDisabilitaRamiCrm: (
      state: CrmRootSliceState,
      action: PayloadAction<string>
    ) => {
      state.disabilitaRamiCrm = action.payload;
    },
    setFirstTreeNode: (
      state: CrmRootSliceState,
      action: PayloadAction<string>
    ) => {
      state.firstTreeNode = action.payload;
    },
    setCrmTree: (
      state: CrmRootSliceState,
      action: PayloadAction<CrmTreeItemInterface[]>
    ) => {
      state.crmTree = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setCrmTree,
  setCrmLoginData,
  setFirstTreeNode,
  setDettaglioEvento,
  setEditEventoCrm,
  setCrmOpenEventInfo,
  setDettaglioEventoEdit,
  setEditCrmNuovo,
  setOpenDetailCrmId,
  setIsDuplicatedTemplate,
  setCodiceFilialeClienteAppImoSelezionato,
  setIsComunicazioneEditingRow,
  setPopupCrmAlertCalled,
  setIsCrmOpened,
  setIsMenuOpened,
  setCrmContatori,
  setImoContatori,
  setDisabilitaRamiCrm,
} = CrmRootSlice.actions;

export const disabilitaRamiMenuCrm = (
  disable: boolean,
  dispatch: AppDispatch,
  disableSection: string
) => {
  dispatch(setDisabilitaRamiCrm(disable ? disableSection : ''));
};

export const modificaMezzoAzione = (
  dettaglioEventoEdit: DettaglioEventoInterface,
  listaAzioni: ListaAzioniInterface[],
  listaMezzi: ListaMezziInterface[],
  idAzione: number,
  idMezzo: number,
  oggettoMezzo: any,
  abilitaAzione?: boolean,
  disabilitaMezzo?: number
) => {
  return (dispatch: AppDispatch) => {
    const newListaAzioni = listaAzioni.map((a) => {
      if (a.idAzione === idAzione) {
        return {
          ...a,
          abilitato: abilitaAzione === undefined ? abilitaAzione : a.abilitato,
          listaMezzi: listaMezzi.map((m) => {
            if (disabilitaMezzo && m.idMezzo === disabilitaMezzo) {
              return {
                ...m,
                abilitato: false,
              };
            }
            if (m.idMezzo === idMezzo) {
              return {
                ...m,
                ...oggettoMezzo,
              };
            }
            return m;
          }),
        };
      }
      return a;
    });

    dispatch(
      setDettaglioEventoEdit({
        ...dettaglioEventoEdit,
        listaAzioni: newListaAzioni,
      })
    );
  };
};

export const nuovoMezzoAzione = (
  dettaglioEventoEdit: DettaglioEventoInterface,
  listaAzioni: ListaAzioniInterface[],
  idAzione: number,
  oggettoMezzo: ListaMezziInterface
) => {
  return (dispatch: AppDispatch) => {
    const newListaAzioni = listaAzioni.map((a) => {
      if (a.idAzione === idAzione) {
        let newListaMezzi = [...a.listaMezzi];
        newListaMezzi.push(oggettoMezzo);
        return {
          ...a,
          listaMezzi: newListaMezzi,
        };
      }
      return a;
    });

    dispatch(
      setDettaglioEventoEdit({
        ...dettaglioEventoEdit,
        listaAzioni: newListaAzioni,
      })
    );
  };
};

export const nuovoFiltroCondizione = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[]
) => {
  return (dispatch: AppDispatch) => {
    //!caso creazione di un filtro non collegato ad altri filtri
    let nuovoOggettoFiltro: ListaGruppoCondizioniFiltroOrInterface = {
      id: nanoid(),
      isDefault: false,
      nomeCondizione: '',
      tipoCondizione: tipoRaggruppamento,
      listaGruppiFiltro: [
        {
          descrizione: '',
          id: nanoid(),
          nome: nomeFiltroPadre,
          listaTipiFiltro: [
            {
              descrizione: '',
              descrizioneEstesa:
                nomeFiltroPadre +
                ' > ' +
                nomeFiltro +
                ' > ' +
                nuovoFiltro
                  .filter((filter) => !!filter.valorefocus)
                  .map((filter) => filter.valoreFiltroApplicato)
                  .join(' - '),
              id: nanoid(),
              idRiga: nanoid(),
              nome: nomeFiltro,
              listaFiltri: nuovoFiltro,
            },
          ],
        },
      ],
    };

    if (tipoRaggruppamento === 'or') {
      const nuovaListaFiltriOr = [
        ...dettaglioEventoEdit.listaGruppoCondizioniFiltroOR,
        nuovoOggettoFiltro,
      ];
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoCondizioniFiltroOR: nuovaListaFiltriOr,
        })
      );
    } else {
      const nuovaListaFiltriNot = [
        ...dettaglioEventoEdit.listaGruppoCondizioniFiltroNOT,
        nuovoOggettoFiltro,
      ];
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoCondizioniFiltroNOT: nuovaListaFiltriNot,
        })
      );
    }
  };
};

export const aggiungiFiltroCondizione = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[],
  idPadre: number | string
) => {
  return (dispatch: AppDispatch) => {
    let listaFiltriApplicati =
      tipoRaggruppamento === 'or'
        ? dettaglioEventoEdit.listaGruppoCondizioniFiltroOR
        : dettaglioEventoEdit.listaGruppoCondizioniFiltroNOT;
    let singleFilter = listaFiltriApplicati.find(
      (filter) => filter.id === idPadre
    );

    if (singleFilter) {
      let newListaFiltriApplicati = [...listaFiltriApplicati].map((filter) => {
        if (filter.id === singleFilter.id) {
          return {
            ...filter,
            listaGruppiFiltro: [
              ...filter.listaGruppiFiltro,
              {
                descrizione: '',
                id: nanoid(),
                nome: nomeFiltroPadre,
                listaTipiFiltro: [
                  {
                    descrizione: '',
                    idRiga: nanoid(),
                    descrizioneEstesa:
                      nomeFiltroPadre +
                      ' > ' +
                      nomeFiltro +
                      ' > ' +
                      nuovoFiltro
                        .filter((filter) => !!filter.valorefocus)
                        .map((filter) => filter.valoreFiltroApplicato)
                        .join(' - '),
                    id: nanoid(),
                    nome: nomeFiltro,
                    listaFiltri: nuovoFiltro,
                  },
                ],
              },
            ],
          };
        }
        return filter;
      });

      if (tipoRaggruppamento === 'or') {
        dispatch(
          setDettaglioEventoEdit({
            ...dettaglioEventoEdit,
            listaGruppoCondizioniFiltroOR: newListaFiltriApplicati,
          })
        );
      } else {
        dispatch(
          setDettaglioEventoEdit({
            ...dettaglioEventoEdit,
            listaGruppoCondizioniFiltroNOT: newListaFiltriApplicati,
          })
        );
      }
    }
  };
};

export const modificaFiltroCondizione = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[],
  modificaFiltro: ListaGruppiFiltroInterface
) => {
  return (dispatch: AppDispatch) => {
    let listaFiltriApplicati =
      tipoRaggruppamento === 'or'
        ? dettaglioEventoEdit.listaGruppoCondizioniFiltroOR
        : dettaglioEventoEdit.listaGruppoCondizioniFiltroNOT;

    const nuovoFiltroSingolo: ListaGruppiFiltroInterface = {
      descrizione: '',
      id: modificaFiltro.id,
      nome: nomeFiltroPadre,
      listaTipiFiltro: [
        {
          descrizione: '',
          descrizioneEstesa:
            nomeFiltroPadre +
            ' > ' +
            nomeFiltro +
            ' > ' +
            nuovoFiltro
              .filter((filter) => !!filter.valorefocus)
              .map((filter) => filter.valoreFiltroApplicato)
              .join(' - '),
          id: modificaFiltro.listaTipiFiltro[0].id,
          idRiga: modificaFiltro.listaTipiFiltro[0].idRiga,
          nome: nomeFiltro,
          listaFiltri: nuovoFiltro,
        },
      ],
    };

    let nuovaListaFiltri = [...listaFiltriApplicati];
    nuovaListaFiltri = nuovaListaFiltri.map((filtroPadre) => {
      if (
        filtroPadre.listaGruppiFiltro.find(
          (group) => group.id === modificaFiltro.id
        )
      ) {
        return {
          ...filtroPadre,
          listaGruppiFiltro: filtroPadre.listaGruppiFiltro.map(
            (singoloGruppo) => {
              if (
                singoloGruppo.id === modificaFiltro.id &&
                singoloGruppo.listaTipiFiltro[0].id ===
                  modificaFiltro.listaTipiFiltro[0].id &&
                singoloGruppo.listaTipiFiltro[0].idRiga ===
                  modificaFiltro.listaTipiFiltro[0].idRiga
              ) {
                return nuovoFiltroSingolo;
              }
              return singoloGruppo;
            }
          ),
        };
      }
      return filtroPadre;
    });

    if (tipoRaggruppamento === 'or') {
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoCondizioniFiltroOR: nuovaListaFiltri,
        })
      );
    } else {
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoCondizioniFiltroNOT: nuovaListaFiltri,
        })
      );
    }
  };
};

export const nuovoFiltroCondizioneTarget = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[]
) => {
  return (dispatch: AppDispatch) => {
    //!caso creazione di un filtro non collegato ad altri filtri
    let nuovoOggettoFiltro: ListaGruppoCondizioniFiltroOrInterface = {
      id: nanoid(),
      isDefault: false,
      nomeCondizione: '',
      tipoCondizione: tipoRaggruppamento,
      listaGruppiFiltro: [
        {
          descrizione: '',
          id: nanoid(),
          nome: nomeFiltroPadre,
          listaTipiFiltro: [
            {
              descrizione: '',
              descrizioneEstesa:
                nomeFiltroPadre +
                ' > ' +
                nomeFiltro +
                ' > ' +
                nuovoFiltro
                  .filter((filter) => !!filter.valorefocus)
                  .map((filter) => filter.valoreFiltroApplicato)
                  .join(' - '),
              id: nanoid(),
              idRiga: nanoid(),
              nome: nomeFiltro,
              listaFiltri: nuovoFiltro,
            },
          ],
        },
      ],
    };

    if (tipoRaggruppamento === 'or') {
      const nuovaListaFiltriOr = [
        ...dettaglioEventoEdit.listaGruppoRetentionOR,
        nuovoOggettoFiltro,
      ];
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoRetentionOR: nuovaListaFiltriOr,
        })
      );
    } else {
      const nuovaListaFiltriNot = [
        ...dettaglioEventoEdit.listaGruppoRetentionNOT,
        nuovoOggettoFiltro,
      ];
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoRetentionNOT: nuovaListaFiltriNot,
        })
      );
    }
  };
};

export const aggiungiFiltroCondizioneTarget = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[],
  idPadre: number | string
) => {
  return (dispatch: AppDispatch) => {
    let listaFiltriApplicati =
      tipoRaggruppamento === 'or'
        ? dettaglioEventoEdit.listaGruppoRetentionOR
        : dettaglioEventoEdit.listaGruppoRetentionNOT;
    let singleFilter = listaFiltriApplicati.find(
      (filter) => filter.id === idPadre
    );

    if (singleFilter) {
      let newListaFiltriApplicati = [...listaFiltriApplicati].map((filter) => {
        if (filter.id === singleFilter.id) {
          return {
            ...filter,
            listaGruppiFiltro: [
              ...filter.listaGruppiFiltro,
              {
                descrizione: '',
                id: nanoid(),
                nome: nomeFiltroPadre,
                listaTipiFiltro: [
                  {
                    descrizione: '',
                    descrizioneEstesa:
                      nomeFiltroPadre +
                      ' > ' +
                      nomeFiltro +
                      ' > ' +
                      nuovoFiltro
                        .filter((filter) => !!filter.valorefocus)
                        .map((filter) => filter.valoreFiltroApplicato)
                        .join(' - '),
                    id: nanoid(),
                    idRiga: nanoid(),
                    nome: nomeFiltro,
                    listaFiltri: nuovoFiltro,
                  },
                ],
              },
            ],
          };
        }
        return filter;
      });

      if (tipoRaggruppamento === 'or') {
        dispatch(
          setDettaglioEventoEdit({
            ...dettaglioEventoEdit,
            listaGruppoRetentionOR: newListaFiltriApplicati,
          })
        );
      } else {
        dispatch(
          setDettaglioEventoEdit({
            ...dettaglioEventoEdit,
            listaGruppoRetentionNOT: newListaFiltriApplicati,
          })
        );
      }
    }
  };
};

export const modificaFiltroCondizioneTarget = (
  tipoRaggruppamento: string,
  nomeFiltroPadre: string,
  nomeFiltro: string,
  dettaglioEventoEdit: DettaglioEventoInterface,
  nuovoFiltro: getFiltriDataInterface[],
  modificaFiltro: ListaGruppiFiltroInterface
) => {
  return (dispatch: AppDispatch) => {
    let listaFiltriApplicati =
      tipoRaggruppamento === 'or'
        ? dettaglioEventoEdit.listaGruppoRetentionOR
        : dettaglioEventoEdit.listaGruppoRetentionNOT;

    const nuovoFiltroSingolo: ListaGruppiFiltroInterface = {
      descrizione: '',
      id: modificaFiltro.id,
      nome: nomeFiltroPadre,
      listaTipiFiltro: [
        {
          descrizione: '',
          descrizioneEstesa:
            nomeFiltroPadre +
            ' > ' +
            nomeFiltro +
            ' > ' +
            nuovoFiltro
              .filter((filter) => !!filter.valorefocus)
              .map((filter) => filter.valoreFiltroApplicato)
              .join(' - '),
          id: modificaFiltro.listaTipiFiltro[0].id,
          idRiga: modificaFiltro.listaTipiFiltro[0].idRiga,
          nome: nomeFiltro,
          listaFiltri: nuovoFiltro,
        },
      ],
    };

    let nuovaListaFiltri = [...listaFiltriApplicati];
    nuovaListaFiltri = nuovaListaFiltri.map((filtroPadre) => {
      if (
        filtroPadre.listaGruppiFiltro.find(
          (group) => group.id === modificaFiltro.id
        )
      ) {
        return {
          ...filtroPadre,
          listaGruppiFiltro: filtroPadre.listaGruppiFiltro.map(
            (singoloGruppo) => {
              if (
                singoloGruppo.id === modificaFiltro.id &&
                singoloGruppo.listaTipiFiltro[0].id ===
                  modificaFiltro.listaTipiFiltro[0].id &&
                singoloGruppo.listaTipiFiltro[0].idRiga ===
                  modificaFiltro.listaTipiFiltro[0].idRiga
              ) {
                return nuovoFiltroSingolo;
              }
              return singoloGruppo;
            }
          ),
        };
      }
      return filtroPadre;
    });

    if (tipoRaggruppamento === 'or') {
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoRetentionOR: nuovaListaFiltri,
        })
      );
    } else {
      dispatch(
        setDettaglioEventoEdit({
          ...dettaglioEventoEdit,
          listaGruppoRetentionNOT: nuovaListaFiltri,
        })
      );
    }
  };
};

export default CrmRootSlice.reducer;
